import { Button } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import type { HealthCheck } from "#/types/dataHealth";
import { CHECK_META, IMPACT_THEME } from "../healthCatalog";
import HealthRowItem from "./HealthRowItem";

interface Props {
  check: HealthCheck;
}

/** One line of the explanation: a fixed label, then the sentence for this check. */
const Line = ({ label, text }: { label: string; text: string }) => (
  <p className="text-sm text-gray-700">
    <span className="font-medium text-gray-900">{label} </span>
    {text}
  </p>
);

/**
 * One failing check, for the client's own users: what is missing, what it
 * breaks today, what filling it in fixes. Same three lines on every card.
 * Then the affected records as compact items that open the page to fix them.
 */
const HealthCheckCard = ({ check }: Props) => {
  const t = usePrvTranslation();
  const navigate = useNavigate();
  const meta = CHECK_META[check.key];
  const theme = IMPACT_THEME[check.impact];
  const Icon = meta.icon;

  return (
    <section
      className={`flex flex-col gap-4 rounded-xl border border-l-4 border-gray-200 bg-white p-5 ${theme.cardBorder}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${theme.iconWrap}`}
          >
            <Icon size={20} stroke={1.8} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">
                {t(`dataHealth.checks.${check.key}.title`)}
              </h3>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${theme.badge}`}
              >
                {check.count}
              </span>
            </div>
            <Line
              label={t("dataHealth.labels.missing")}
              text={t(`dataHealth.checks.${check.key}.missing`)}
            />
            <Line
              label={t("dataHealth.labels.breaks")}
              text={t(`dataHealth.checks.${check.key}.breaks`)}
            />
            <Line
              label={t("dataHealth.labels.fixes")}
              text={t(`dataHealth.checks.${check.key}.fixes`)}
            />
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
          {t("dataHealth.fixOn", { page: t(`dataHealth.pages.${meta.page}`) })}
        </span>
      </header>

      <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
        {check.rows.map((row) => (
          <li key={String(row.id)}>
            <HealthRowItem row={row} href={meta.recordHref(row.id)} countOf={meta.countOf} />
          </li>
        ))}
      </ul>

      {check.truncated && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <span className="text-xs text-gray-500">
            {t("dataHealth.showing", { shown: check.rows.length, count: check.count })}
          </span>
          {meta.listHref && (
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => void navigate(meta.listHref!)}
            >
              {t("dataHealth.openList")}
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

export default HealthCheckCard;
